import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Flame, PauseCircle, PlayCircle, Trash2 } from "lucide-react";
import {
  errorMessage, fetchWarmup, fetchAttribution, formatNumber, formatRate, formatDateTime,
  saveWarmupPlan, runWarmupWave, pauseWarmupPlan, resumeWarmupPlan, deleteWarmupPlan, WARMUP_KEY,
  type WarmupPlanStatus, type WaveResult, type WarmupResponse, type AttributionResponse, type JourneysResponse,
} from "@/lib/comms";
import { CardsSkeleton, EmptyState, ErrorState } from "./shared";

function invalidate() {
  queryClient.invalidateQueries({ queryKey: WARMUP_KEY });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/attribution"] });
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "danger" | "good" }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${tone === "danger" ? "text-destructive" : tone === "good" ? "text-primary" : ""}`}>{value}</p>
      {sub ? <p className="text-xs text-muted-foreground leading-snug">{sub}</p> : null}
    </div>
  );
}

function CreatePlanDialog({ defaultRamp }: { defaultRamp: number[] }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [journeyId, setJourneyId] = useState("");
  const [tag, setTag] = useState("brevo-import-2026-09-06");
  const [ramp, setRamp] = useState(defaultRamp.join(", "));
  const [dailyCap, setDailyCap] = useState("40000");
  const [offerCode, setOfferCode] = useState("PEPCHECK");

  const journeys = useQuery<JourneysResponse>({ queryKey: ["/api/admin/comms/journeys"] });

  const save = useMutation({
    mutationFn: () => saveWarmupPlan({
      id: name || journeyId,
      name: name || undefined,
      journeyId,
      tag: tag.trim() || null,
      ramp: ramp.split(",").map(s => parseInt(s.trim(), 10)).filter(n => n > 0),
      dailyCap: parseInt(dailyCap, 10),
      offerCode: offerCode || null,
      status: "paused",
    }),
    onSuccess: () => {
      invalidate();
      setOpen(false);
      toast({ title: "Warm-up plan saved", description: "It starts paused. Read a test of every email in the journey, then start it." });
    },
    onError: (e: unknown) => toast({ title: "Could not save the plan", description: errorMessage(e), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="button-new-warmup">New warm-up plan</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New warm-up plan</DialogTitle>
          <DialogDescription>
            A plan releases cold leads into a journey one wave a day, most recently engaged first. It starts paused and it
            stops itself if a wave bounces or draws complaints.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="warmup-name">Name</Label>
            <Input id="warmup-name" value={name} onChange={e => setName(e.target.value)} placeholder="PEP 2027 re-engagement" data-testid="input-warmup-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Journey</Label>
            <Select value={journeyId} onValueChange={setJourneyId}>
              <SelectTrigger data-testid="select-warmup-journey"><SelectValue placeholder="Pick the journey to release into" /></SelectTrigger>
              <SelectContent>
                {(journeys.data?.journeys || []).map(j => (
                  <SelectItem key={j.id} value={j.id}>{j.name}{j.enabled ? "" : " (off)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Use a journey triggered by "Released by a warm-up plan", so nothing enrols on its own.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="warmup-tag">Contact tag</Label>
            <Input id="warmup-tag" value={tag} onChange={e => setTag(e.target.value)} data-testid="input-warmup-tag" />
            <p className="text-xs text-muted-foreground">Only leads carrying this tag are eligible.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="warmup-ramp">Ramp (one wave a day)</Label>
              <Input id="warmup-ramp" value={ramp} onChange={e => setRamp(e.target.value)} data-testid="input-warmup-ramp" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warmup-cap">Daily cap</Label>
              <Input id="warmup-cap" value={dailyCap} onChange={e => setDailyCap(e.target.value)} data-testid="input-warmup-cap" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="warmup-offer">Offer code recorded on every send</Label>
            <Input id="warmup-offer" value={offerCode} onChange={e => setOfferCode(e.target.value.toUpperCase())} data-testid="input-warmup-offer" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!journeyId || save.isPending} data-testid="button-save-warmup">
            {save.isPending ? "Saving…" : "Create, paused"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanCard({ s }: { s: WarmupPlanStatus }) {
  const { toast } = useToast();
  const [preview, setPreview] = useState<WaveResult | null>(null);
  const plan = s.plan;

  const run = useMutation({
    mutationFn: (live: boolean) => runWarmupWave(plan.id, !live),
    onSuccess: (result, live) => {
      setPreview(result);
      if (live) {
        invalidate();
        toast({
          title: result.ran ? `Wave ${result.waveIndex} released` : "Nothing went out",
          description: result.ran ? `${formatNumber(result.enrolled)} contacts enrolled. The queue drains within the daily cap and quiet hours.` : result.reason || "",
          variant: result.ran ? undefined : "destructive",
        });
      }
    },
    onError: (e: unknown) => toast({ title: "Could not run the wave", description: errorMessage(e), variant: "destructive" }),
  });

  const pause = useMutation({
    mutationFn: () => pauseWarmupPlan(plan.id, "Paused from the console"),
    onSuccess: () => { invalidate(); toast({ title: "Plan paused", description: "No more waves until you start it again. Anything already queued still goes out." }); },
    onError: (e: unknown) => toast({ title: "Could not pause", description: errorMessage(e), variant: "destructive" }),
  });

  const resume = useMutation({
    mutationFn: () => resumeWarmupPlan(plan.id),
    onSuccess: () => { invalidate(); toast({ title: "Plan started", description: "The next wave goes out on the next hourly pass." }); },
    onError: (e: unknown) => toast({ title: "Could not start the plan", description: errorMessage(e), variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: () => deleteWarmupPlan(plan.id),
    onSuccess: () => { invalidate(); toast({ title: "Plan deleted", description: "Journeys, enrolments and sends are untouched." }); },
    onError: (e: unknown) => toast({ title: "Could not delete the plan", description: errorMessage(e), variant: "destructive" }),
  });

  const last = s.lastWave;
  const audienceLine = [plan.audience.tag && `tag ${plan.audience.tag}`, plan.audience.listId && "a list", plan.audience.source && `source ${plan.audience.source}`].filter(Boolean).join(", ");

  return (
    <Card className={plan.status === "paused" && plan.pausedReason ? "border-destructive" : ""} data-testid={`card-warmup-${plan.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Flame className="w-4 h-4" /> {plan.name}
              <Badge variant={plan.status === "active" ? "default" : plan.status === "completed" ? "secondary" : "outline"}>{plan.status}</Badge>
            </CardTitle>
            <CardDescription>
              Wave {plan.waveIndex} of the ramp {plan.ramp.map(formatNumber).join(", ")}, then {formatNumber(plan.ramp[plan.ramp.length - 1])} a day.
              Audience: {audienceLine || "not set"}. Offer {plan.offerCode || "none"}.
              Stops itself over {plan.bounceRatePct}% hard bounces or {plan.complaintRatePct}% complaints.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={() => run.mutate(false)} disabled={run.isPending} data-testid={`button-warmup-dryrun-${plan.id}`}>
              Preview next wave
            </Button>
            {plan.status === "active" ? (
              <Button type="button" variant="outline" onClick={() => pause.mutate()} disabled={pause.isPending} data-testid={`button-warmup-pause-${plan.id}`}>
                <PauseCircle className="w-4 h-4 mr-2" /> Pause
              </Button>
            ) : (
              <Button type="button" onClick={() => resume.mutate()} disabled={resume.isPending || plan.status === "completed"} data-testid={`button-warmup-resume-${plan.id}`}>
                <PlayCircle className="w-4 h-4 mr-2" /> Start
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => run.mutate(true)} disabled={run.isPending || !s.nextWaveAllowedToday} data-testid={`button-warmup-run-${plan.id}`}>
              Send wave now
            </Button>
            <Button type="button" variant="ghost" onClick={() => remove.mutate()} disabled={remove.isPending} data-testid={`button-warmup-delete-${plan.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {plan.pausedReason ? (
          <p className="text-sm text-destructive flex items-start gap-2" data-testid={`text-warmup-paused-${plan.id}`}>
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {plan.pausedReason}
          </p>
        ) : null}
        {!s.nextWaveAllowedToday && s.nextWaveBlockedBy ? (
          <p className="text-sm text-muted-foreground" data-testid={`text-warmup-blocked-${plan.id}`}>Next wave held: {s.nextWaveBlockedBy}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Next wave: up to {formatNumber(s.nextWaveSize)} contacts, allowed today.</p>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Still eligible" value={formatNumber(s.audience.remaining)} sub={`of ${formatNumber(s.audience.total)} tagged`} />
          <Tile label="Enrolled so far" value={formatNumber(s.totals.enrolled)} sub={`${plan.waveIndex} waves`} />
          <Tile label="Sent" value={formatNumber(s.totals.sent)} sub={`${formatNumber(s.totals.opened)} opened, ${formatNumber(s.totals.clicked)} clicked`} />
          <Tile label="Paid" value={formatNumber(s.totals.paid)} sub={`${formatNumber(s.totals.signups)} signups`} tone={s.totals.paid ? "good" : undefined} />
        </div>

        {s.audience.unconfirmed > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {formatNumber(s.audience.unconfirmed)} leads are marketable but not opt-in confirmed, so they are skipped. Run
            <span className="font-mono text-xs"> scripts/comms/seed-acquisition.ts --confirm-brevo-consent</span> to record the consent they gave on Brevo.
          </p>
        )}

        {last && (
          <div className="rounded-md border p-3 space-y-1">
            <p className="text-sm font-semibold">Last wave, number {last.index}</p>
            <p className="text-sm text-muted-foreground">
              {formatNumber(last.attempted)} settled, {formatNumber(last.queued)} still queued. Hard bounces {formatRate(last.bounceRate)},
              complaints {formatRate(last.complaintRate)}, unsubscribes {formatNumber(last.unsubscribed)}. Opens {formatNumber(last.opened)},
              clicks {formatNumber(last.clicked)}.
              {last.settled ? "" : " Not enough of it has landed to judge yet."}
            </p>
            {last.breach ? <p className="text-sm text-destructive">{last.breach}</p> : null}
          </div>
        )}

        {preview && (
          <div className="rounded-md border p-3 space-y-1" data-testid={`preview-warmup-${plan.id}`}>
            <p className="text-sm font-semibold">{preview.dryRun ? "Preview" : "Result"} of wave {preview.waveIndex}</p>
            <p className="text-sm text-muted-foreground">
              {preview.reason
                ? preview.reason
                : `${formatNumber(preview.enrolled)} contacts, allowed size ${formatNumber(preview.size)}.${preview.sample.length ? ` First few: ${preview.sample.join(", ")}` : ""}`}
            </p>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Last wave {plan.lastWaveAt ? formatDateTime(plan.lastWaveAt) : "never"}. One wave per day, quiet hours and the circuit breaker apply, and
          nobody is enrolled twice.
        </p>
      </CardContent>
    </Card>
  );
}

function AttributionTable() {
  const { data, isLoading, error } = useQuery<AttributionResponse>({
    queryKey: ["/api/admin/comms/attribution"],
    queryFn: () => fetchAttribution(),
  });
  const rows = useMemo(() => data?.waves || [], [data]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">What each wave and offer produced</CardTitle>
        <CardDescription>
          Counted off the send rows themselves, so every number points back at a specific message. A signup is an account
          created with the address we emailed; paid is that account's first successful payment inside the window.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? <ErrorState error={error} /> : isLoading ? <CardsSkeleton count={2} /> : !rows.length ? (
          <EmptyState title="Nothing attributed yet" hint="Numbers appear here once a wave has gone out and the hourly attribution pass has run." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Wave</TableHead>
                  <TableHead>Step</TableHead>
                  <TableHead>Offer</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Opened</TableHead>
                  <TableHead className="text-right">Clicked</TableHead>
                  <TableHead className="text-right">Bounced</TableHead>
                  <TableHead className="text-right">Complaints</TableHead>
                  <TableHead className="text-right">Unsubs</TableHead>
                  <TableHead className="text-right">Signups</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.plan}-${r.wave}-${r.step}-${i}`}>
                    <TableCell className="whitespace-nowrap">{r.plan || "–"}</TableCell>
                    <TableCell>{r.wave ?? "–"}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.step || "–"}</TableCell>
                    <TableCell>{r.offerCode || "–"}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.sent)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.opened)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.clicked)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.bounced)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.complained)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.unsubscribed)}</TableCell>
                    <TableCell className="text-right">{formatNumber(r.signups)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatNumber(r.paid)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function WarmupTab() {
  const { data, isLoading, error } = useQuery<WarmupResponse>({ queryKey: WARMUP_KEY, queryFn: fetchWarmup, refetchInterval: 60_000 });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Warm-up</CardTitle>
              <CardDescription className="max-w-3xl">
                A single blast to hundreds of thousands of cold addresses would get the domain filtered at Gmail and take
                transactional mail to paying students down with it. So a plan releases leads into a journey one wave a day,
                most recently engaged first, on a ramp. It never sends to the same contact twice, it skips anyone suppressed
                or opted out, it honours quiet hours and the daily cap, and it pauses itself if a wave turns bad.
              </CardDescription>
            </div>
            {data ? <CreatePlanDialog defaultRamp={data.defaultRamp} /> : null}
          </div>
        </CardHeader>
      </Card>

      {error ? <ErrorState error={error} /> : isLoading || !data ? <CardsSkeleton count={2} className="h-64" /> : !data.plans.length ? (
        <EmptyState
          title="No warm-up plans yet"
          hint="Seed the programme with scripts/comms/seed-acquisition.ts, then create a plan pointing at the re-engagement journey."
        />
      ) : (
        data.plans.map(s => <PlanCard key={s.plan.id} s={s} />)
      )}

      <AttributionTable />
    </div>
  );
}
